import { useCallback, useEffect, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Chip,
	CircularProgress,
	Stack,
	Typography,
} from '@mui/material'
import AvitoAccountFormDialog from './AvitoAccountFormDialog'
import {
	deleteAvitoAccount,
	getAvitoAccountErrorMessage,
	listAvitoAccounts,
} from '../../../utils/avitoAccounts'

function AvitoAccountsSettings() {
	const [accounts, setAccounts] = useState([])
	const [loading, setLoading] = useState(true)
	const [requestError, setRequestError] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [editingAccount, setEditingAccount] = useState(null)
	const [deletingId, setDeletingId] = useState(null)

	const loadAccounts = useCallback(async () => {
		setLoading(true)
		setRequestError('')
		try {
			setAccounts(await listAvitoAccounts())
		} catch (error) {
			setRequestError(getAvitoAccountErrorMessage(error))
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		loadAccounts()
	}, [loadAccounts])

	const openCreate = () => {
		setEditingAccount(null)
		setFormOpen(true)
	}

	const handleSaved = (saved) => {
		setAccounts((current) => {
			const exists = current.some((account) => account.id === saved.id)
			return exists
				? current.map((account) => (account.id === saved.id ? saved : account))
				: [...current, saved]
		})
		setFormOpen(false)
	}

	const handleDelete = async (account) => {
		if (!window.confirm(`Удалить аккаунт «${account.name}»?`)) return
		setDeletingId(account.id)
		setRequestError('')
		try {
			await deleteAvitoAccount(account.id)
			setAccounts((current) => current.filter((item) => item.id !== account.id))
		} catch (error) {
			setRequestError(getAvitoAccountErrorMessage(error))
		} finally {
			setDeletingId(null)
		}
	}

	return (
		<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
			<Stack spacing={2}>
				<Box>
					<Typography variant="h5" gutterBottom>
						Авито
					</Typography>
					<Typography color="text.secondary">
						Локальные аккаунты файловой выгрузки. Это не OAuth и не подключение к
						API Авито.
					</Typography>
				</Box>
				<Box>
					<Button variant="contained" onClick={openCreate}>
						Добавить аккаунт
					</Button>
				</Box>
				{requestError && <Alert severity="error">{requestError}</Alert>}
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
						<CircularProgress />
					</Box>
				) : accounts.length === 0 ? (
					<Alert severity="info">
						Аккаунтов выгрузки пока нет. Добавьте аккаунт, чтобы выбирать его в
						preview объявления.
					</Alert>
				) : (
					<Stack spacing={2}>
						{accounts.map((account) => (
							<Card key={account.id} variant="outlined">
								<CardContent>
									<Stack
										direction={{ xs: 'column', sm: 'row' }}
										spacing={1}
										alignItems={{ xs: 'flex-start', sm: 'center' }}>
										<Typography variant="h6">{account.name}</Typography>
										<Chip
											size="small"
											color={account.status === 'active' ? 'success' : 'default'}
											label={account.status === 'active' ? 'Активен' : 'Отключён'}
										/>
									</Stack>
								</CardContent>
								<CardActions sx={{ flexWrap: 'wrap' }}>
									<Button
										onClick={() => {
											setEditingAccount(account)
											setFormOpen(true)
										}}>
										Редактировать
									</Button>
									<Button
										color="error"
										onClick={() => handleDelete(account)}
										disabled={deletingId === account.id}>
										{deletingId === account.id ? 'Удаление…' : 'Удалить'}
									</Button>
								</CardActions>
							</Card>
						))}
					</Stack>
				)}
			</Stack>
			<AvitoAccountFormDialog
				open={formOpen}
				account={editingAccount}
				onClose={() => setFormOpen(false)}
				onSaved={handleSaved}
			/>
		</Box>
	)
}

export default AvitoAccountsSettings
